import {
  Box,
  CloseButton,
  Flex,
  Icon,
  Input,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  useOutsideClick,
} from "@chakra-ui/react";
import React, { RefObject, useEffect, useRef, useState } from "react";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";

import Button from "./Button";
import Option from "./Option";
import formatOptions, { MultiSelectProps, Options } from "./selectOptions";

const MultiSelect = <T extends Options>({
  name,
  options,
  setValue,
  optionsConfig,
  labelHeight = 15,
  value = [],
  onChange,
  testId = "",
  collapseInput = false,
  maxLength,
  allowCustomInput,
  ...restProps
}: MultiSelectProps<T>) => {
  const menuRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement | null>(
    null
  );
  const boxRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement | null>(null);
  const inputRef: RefObject<HTMLInputElement> = useRef(null);
  const tagRef = useRef<(HTMLSpanElement | null)[]>([]);
  const isInitialMount = useRef(true);

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const { width = "100%", isDisabled, onBlur, ...boxProps } = restProps;
  const [inputHeight, setInputHeight] = useState(
    boxRef.current?.clientHeight || 35
  );
  const [inputWidth, setInputWidth] = useState(boxRef.current?.clientWidth);
  const [tagOffsets, setTagOffsets] = useState(0);

  useOutsideClick({
    ref: menuRef,
    handler: () => {
      setIsOpen(false);
      setInputValue("");
    },
  });

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (onChange) {
      onChange(value);
    }
    setInputHeight(boxRef.current?.clientHeight || 35);
    setInputWidth(boxRef.current?.clientWidth);
    setTagOffsets(
      tagRef.current.filter(ref => ref && ref.offsetTop > 35).length
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleBoxClick = () => {
    inputRef.current?.focus();
    setInputWidth(boxRef.current?.clientWidth);
  };

  const handleRemove = (itemValue: string) => {
    setValue(value.filter(item => item !== itemValue));
    setTagOffsets(
      tagRef.current.filter(ref => ref && ref.offsetTop > 5).length
    );
  };

  const handleOptionClick = (itemValue: string) => {
    handleBoxClick();
    setInputValue("");
    if (!maxLength || maxLength !== value.length)
      setValue([...value, itemValue]);
  };

  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // If there is a highlighted index, scroll to the corresponding option element
  useEffect(() => {
    if (highlightedIndex >= 0) {
      const highlightedOption = document.getElementById(
        `option-${highlightedIndex}`
      );
      highlightedOption?.scrollIntoView({
        block: "nearest",
        inline: "nearest",
      });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(prevIndex =>
        prevIndex === remainingOptions.length - 1 ? 0 : prevIndex + 1
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(prevIndex =>
        prevIndex <= 0 ? remainingOptions.length - 1 : prevIndex - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && remainingOptions[highlightedIndex]) {
        handleOptionClick(
          remainingOptions[highlightedIndex].value as T[keyof T]
        );
        setHighlightedIndex(prevIndex => prevIndex - 1);
      } else if (showCustomInputs) {
        if (!value.includes(inputValue as T[keyof T])) {
          handleOptionClick(inputValue as T[keyof T]);
        }
      }
      setInputValue("");
    } else if (e.key === "Tab") {
      setIsOpen(false);
      setHighlightedIndex(-1);
    } else if (
      e.key === "Backspace" &&
      value.length > 0 &&
      inputValue.length === 0
    ) {
      const newState = [...value];
      newState.pop();
      setValue(newState);
    }
  };

  const selectOptions = formatOptions<Options>(options, {
    ...optionsConfig,
    unspecified: optionsConfig?.unspecified || "REMOVE",
  }).map(([label, value]) => ({ label, value }));

  const remainingOptions = selectOptions.filter(
    option =>
      !value.includes(option.value as T[keyof T]) &&
      option.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleTagRef = (tag: HTMLSpanElement | null, index: number) => {
    if (tag) {
      const tagRefArray = tagRef.current;
      tagRefArray[index] = tag;
    }
  };

  const handleTagClose = (index: number) => {
    const tagRefArray = tagRef.current;
    tagRefArray.splice(index, 1);
  };

  const showCustomInputs =
    allowCustomInput &&
    selectOptions.length === 0 &&
    inputValue.length > 0 &&
    maxLength !== value.length;

  const noResultsFound =
    remainingOptions.length === 0 &&
    !showCustomInputs &&
    maxLength !== value.length;

  return (
    <Flex flexDirection="column">
      <Box
        ref={boxRef}
        bgColor="white"
        border="1px solid"
        borderColor={restProps?.isInvalid ? "red.500" : "gray.400"}
        display="flex"
        flex="1 1 0%"
        flexWrap="wrap"
        overflow="hidden"
        maxWidth={width}
        width={width}
        maxHeight={!isOpen && collapseInput ? "35px" : "auto"}
        zIndex={isOpen ? 4 : 2}
        onClick={() => {
          handleBoxClick();
          if (!isDisabled) setIsOpen(true);
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="multi-select-options"
        aria-owns="multi-select-options"
        aria-label={restProps["aria-label"] || "Multi-select"}
        {...boxProps}
      >
        <Flex width="100%" justifyContent="space-between">
          <Flex overflow="hidden" flexWrap="wrap">
            {value.map((selectedItem, index) => {
              const label = allowCustomInput
                ? selectedItem
                : selectOptions.find(option => option.value === selectedItem)
                    ?.label;
              return (
                <Tooltip key={selectedItem} label={label} openDelay={750}>
                  <Tag
                    m={1}
                    w="auto"
                    h="25px"
                    key={selectedItem}
                    ref={tag => handleTagRef(tag, index)}
                    data-testid={`${testId}-multiselect-selected`}
                  >
                    <TagLabel>{label}</TagLabel>
                    <TagCloseButton
                      onClick={() => {
                        handleRemove(selectedItem);
                        handleTagClose(index);
                      }}
                      aria-label={`Remove ${selectedItem}`}
                      data-testid={`${testId}-multiselect-clear-${selectedItem}`}
                    />
                  </Tag>
                </Tooltip>
              );
            })}
            <Input
              name={name}
              bgColor="transparent"
              flex={1}
              outline={0}
              minWidth="5px"
              border="none"
              boxShadow="none !important"
              sx={{
                _focus: {
                  border: "none",
                },
              }}
              onFocus={() => setIsOpen(true)}
              value={inputValue}
              ref={inputRef}
              onBlur={e => {
                onBlur && onBlur(e);
                setValue(value);
              }}
              onChange={e => {
                setInputValue(e.target.value);
                setHighlightedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              required={false}
              data-testid={`${testId}-multiselect-input`}
              role="textbox"
              aria-autocomplete="list"
              aria-controls="multi-select-options"
              aria-activedescendant={
                highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined
              }
              aria-label="Type to filter options"
            />
          </Flex>
          <Flex alignItems="center" h="35px">
            {collapseInput && !isOpen && tagOffsets > 0 ? (
              <Text
                w="25px"
                h="25px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                backgroundColor="blue.400"
                borderRadius="50%"
                color="white"
              >
                +{tagOffsets}
              </Text>
            ) : (
              <Box w="25px" />
            )}
            {value.length > 0 && (
              <CloseButton
                onClick={() => setValue([])}
                aria-label="Clear all selections"
                data-testid={`${testId}-multiselect-clear-all`}
                size="sm"
                fontSize="8px"
              />
            )}
            <Box
              borderLeft="1px"
              borderColor="gray.400"
              px={1}
              display="flex"
              cursor="pointer"
              data-testid={`${testId}-multiselect-toggle`}
            >
              <Icon
                as={isOpen ? FaCaretUp : FaCaretDown}
                fontSize="21px"
                color="gray.400"
              />
            </Box>
          </Flex>
        </Flex>
      </Box>
      {isOpen && (
        <Box
          ref={menuRef}
          bgColor="white"
          border="1px solid"
          borderColor="gray.400"
          position="absolute"
          top={`${labelHeight + (inputHeight || 0)}px`}
          width={inputWidth ? inputWidth + 2 : width}
          zIndex={5}
          maxHeight="200px"
          overflow="auto"
          role="listbox"
          id="multi-select-options"
          aria-labelledby={restProps["aria-labelledby"]}
        >
          {remainingOptions.length > 0 &&
            (!maxLength || maxLength !== value.length) &&
            remainingOptions.map(({ label, value: itemValue }, index) => (
              <Option
                label={label}
                itemValue={itemValue}
                handleClick={handleOptionClick}
                highlightedIndex={highlightedIndex}
                index={index}
                key={itemValue}
                testId={testId}
              />
            ))}
          {showCustomInputs && (
            <Flex
              alignItems="center"
              justifyContent="space-between"
              padding="0.5rem"
            >
              <Text>{inputValue}</Text>
              <Button
                onClick={() => {
                  if (!value.includes(inputValue as T[keyof T])) {
                    setValue([...value, inputValue] as T[keyof T][]);
                  }
                  setInputValue("");
                  setIsOpen(false);
                  handleBoxClick();
                }}
              >
                Add
              </Button>
            </Flex>
          )}
          {noResultsFound && <Text padding="0.5rem">No results found</Text>}
          {maxLength === value.length && (
            <Text padding="0.5rem">
              You have reached the maximum number of selections
            </Text>
          )}
        </Box>
      )}
    </Flex>
  );
};

export default MultiSelect;
